import React from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  Box,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Minus, Plus, X } from 'lucide-react';
import { Product } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

interface CartItem {
  product: Product;
  quantity: number;
}

interface CartProps {
  items: CartItem[];
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemoveItem: (productId: string) => void;
  onCheckout: () => void;
  isProcessing: boolean;
}

export function Cart({ items, onUpdateQuantity, onRemoveItem, onCheckout, isProcessing }: CartProps) {
  const { t } = useLanguage();

  const total = items.reduce((sum, item) => sum + (item.product.salePrice * item.quantity), 0);

  return (
    <Paper className="p-4 h-full flex flex-col">
      <Typography variant="h6" component="h2" className="mb-4">
        {t.cart} ({items.length})
      </Typography>

      {items.length === 0 ? (
        <Typography color="text.secondary" className="text-center my-8">
          {t.emptyCart}
        </Typography>
      ) : (
        <>
          <List className="flex-1 overflow-auto">
            {items.map((item) => (
              <ListItem
                key={item.product._id}
                className="flex items-center gap-4 py-3"
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => onRemoveItem(item.product._id)}
                  >
                    <X size={18} />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={item.product.name}
                  secondary={`${item.product.salePrice} FC`}
                />
                <Box className="flex items-center gap-2">
                  <IconButton
                    size="small"
                    onClick={() => onUpdateQuantity(item.product._id, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                  >
                    <Minus size={16} />
                  </IconButton>
                  <Typography>{item.quantity}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => onUpdateQuantity(item.product._id, item.quantity + 1)}
                    disabled={item.quantity >= item.product.quantity}
                  >
                    <Plus size={16} />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>

          <Divider className="my-4" />

          <Box className="mt-4">
            <Box className="flex justify-between mb-4">
              <Typography variant="h6">{t.total}:</Typography>
              <Typography variant="h6">{total} FC</Typography>
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={onCheckout}
              disabled={isProcessing}
              sx={{
                bgcolor: '#1F2937',
                color: 'white',
                '&:hover': {
                  bgcolor: '#374151'
                }
              }}
            >
              {isProcessing ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t.checkout
              )}
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}
